/**
 * 生成失败 toast：编辑区与无限画布共用。
 * 以 noticeKey（优先 jobId）去重，避免画布 sync 先 failTask 后预览轮询再弹一次。
 */

import { detectOmniTaskTypeConstraintMismatch } from '@/lib/conversations/omni-reference-task-mismatch';
import { useOmniTaskMismatchStore } from '@/stores/omni-task-mismatch-store';

export const AI_GENERATION_FAILURE_TOAST_TITLE = '生成失败';
export const AI_GENERATION_FAILURE_FALLBACK_MESSAGE = '生成任务失败，请重试';
export const AI_VOICEOVER_GENERATION_FAILURE_FALLBACK_MESSAGE = '配音生成失败，请重试';
export const AI_SOUND_EFFECT_GENERATION_FAILURE_FALLBACK_MESSAGE = '音效生成失败，请重试';

export type AiGenerationFailureToastFn = (props: {
  title?: string;
  description?: string;
  variant?: 'default' | 'destructive';
}) => unknown;

const notifiedFailureNoticeKeys = new Set<string>();

/** 测试或热更新时可清空去重集合。 */
export function resetAiGenerationFailureNoticesForTests(): void {
  notifiedFailureNoticeKeys.clear();
}

export function forgetAiGenerationFailureNotice(noticeKey: string): void {
  const key = noticeKey.trim();
  if (!key) return;
  notifiedFailureNoticeKeys.delete(key);
}

/**
 * 从 AI job / 轮询 payload 解析失败文案；兼容 camelCase 与 snake_case 接口字段。
 */
export function resolveAiGenerationFailureMessage(
  payload: unknown,
  fallback: string = AI_GENERATION_FAILURE_FALLBACK_MESSAGE
): string {
  if (!payload || typeof payload !== 'object') return fallback;
  const record = payload as Record<string, unknown>;
  const errorMessage = record.errorMessage;
  if (typeof errorMessage === 'string' && errorMessage.trim()) {
    return errorMessage.trim();
  }
  return fallback;
}

/**
 * 同一 noticeKey 只 toast 一次。返回是否实际发出通知。
 *
 * omniMismatchRedirect 必须由调用方显式开启：只有能保证 Composer 会消费错配弹窗的
 * 视频轮询路径可以用它替代 toast，其余路径开启会让失败静默丢失。
 */
export function notifyAiGenerationFailureOnce(params: {
  noticeKey: string;
  description?: string;
  payload?: unknown;
  omniMismatchRedirect?: boolean;
  toast: AiGenerationFailureToastFn;
  fallbackDescription?: string;
}): boolean {
  const key = params.noticeKey.trim();
  if (!key) return false;
  if (notifiedFailureNoticeKeys.has(key)) return false;
  notifiedFailureNoticeKeys.add(key);

  const description =
    (typeof params.description === 'string' && params.description.trim()
      ? params.description.trim()
      : null) ??
    params.fallbackDescription ??
    AI_GENERATION_FAILURE_FALLBACK_MESSAGE;

  if (params.omniMismatchRedirect) {
    const suggestedMode = detectOmniTaskTypeConstraintMismatch({
      errorMessage: description,
      payload: params.payload,
    });
    // 只有真的存在能弹引导的 Composer 才用弹窗替代 toast，否则失败会静默丢失。
    const store = useOmniTaskMismatchStore.getState();
    if (suggestedMode && store.canConsume(suggestedMode)) {
      store.queueErrorMismatch(suggestedMode, {
        title: AI_GENERATION_FAILURE_TOAST_TITLE,
        description,
        toast: params.toast,
      });
      return true;
    }
  }

  params.toast({
    title: AI_GENERATION_FAILURE_TOAST_TITLE,
    description,
    variant: 'destructive',
  });
  return true;
}
